import { IDocumentSearchCriteria } from '../../../../customProxy/retailExtension/DataServiceEntities.g';
import { getDocumentAvailabilityAsync } from '../../../../customProxy/retailExtension/DataActionExtension.g';
import { format, IContext, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { IBuyboxProps, IBuyboxResources } from '../../definition-extensions/buybox.ext.props.autogenerated';
import { IBuyboxData } from '../../views/buybox.data';
import { IBuyboxViewProps } from '@msdyn365-commerce-modules/buybox';
import { PropertyType } from '../propertyTypeEnum';
import { StatusTypes } from '../types/buybox';
import { v4 as uuid } from 'uuid';

interface IStatusAndCorrelation {
    status: StatusTypes;
    correlationId?: string;
}

const generateErrorMessage = (failedResult: IStatusAndCorrelation, product: SimpleProduct, resources: IBuyboxResources) => {
    const {
        productNotFoundErrorMessage,
        duplicateInStateErrorMessage,
        duplicateInCartErrorMessage,
        somethingWentWrongErrorMessage
    } = resources;
    const { status, correlationId } = failedResult;
    const name = product.Name;
    const message =
        status === StatusTypes.NotFound
            ? productNotFoundErrorMessage
            : status === StatusTypes.DuplicateInState
            ? duplicateInStateErrorMessage
            : status === StatusTypes.DuplicateInCart
            ? duplicateInCartErrorMessage
            : somethingWentWrongErrorMessage;
    return format(message, name, status, correlationId);
};
const getDocumentResult = async (requestData: IDocumentSearchCriteria, ctx: IContext) => {
    const result = {
        status: StatusTypes.Success,
        correlationId: requestData.CorrelationId
    };
    try {
        const response = await getDocumentAvailabilityAsync(ctx, requestData);
        if (response.IsSuccess) {
            if (response.DocumentData?.length) {
                return { ...result, documents: response };
            } else {
                result.status = StatusTypes.NotFound;
            }
        } else if (response.Status === '404') {
            result.status = StatusTypes.NotFound;
        } else {
            result.status = StatusTypes.IntegrationError;
        }
    } catch (e) {
        const error = e as any;
        const status = error.status;
        if (status === 'Service Unavailable') {
            result.status = StatusTypes.Canceled;
        } else {
            result.status = StatusTypes.CRTError;
        }
    }
    return { ...result, documents: undefined };
};

const getDocumentList = async (
    restProps: React.PropsWithChildren<IBuyboxViewProps & IBuyboxProps<IBuyboxData>>,
    product: SimpleProduct,
    valueToGetData: string,
    propertyType: number,
    displayErrorMessage: (message: string) => void,
    clearValues: () => void
) => {
    const requestData: IDocumentSearchCriteria = {
        Id: 0,
        QueryType: propertyType,
        Address: [],
        LegalDescription: [],
        LegalDescriptionCondo: [],
        CPID: [],
        TitleNumber: [],
        LincNumber: [],
        RollNumber: [],
        ATSNos: [],
        BlockProfileNumbers: [],
        Communities: [],
        Quadrants: [],
        FileType: '',
        ProductName: '',
        ProductType: '',
        CorrelationId: uuid()
    };
    // getting all product types from app config
    const ctx: IContext = { callerContext: restProps.context.actionContext };
    const { productTypePA, fileTypePA, tibcoProductNamePA } = restProps.context.app.config;
    const prodType: string =
        restProps.data.productAttributes.result
            ?.find(x => x.Name === productTypePA)
            ?.TextValue?.toLowerCase()
            .trim() || '';

    switch (propertyType) {
        case PropertyType.community:
            if (PropertyType[PropertyType.document].toLowerCase() === prodType) {
                const fileType: string = restProps.data.productAttributes.result?.find(x => x.Name === fileTypePA)?.TextValue || '';
                const productName: string =
                    restProps.data.productAttributes.result?.find(x => x.Name === tibcoProductNamePA)?.TextValue || '';

                requestData.FileType = fileType;
                requestData.ProductName = productName;
            }

            requestData.Communities = [valueToGetData];
            requestData.ProductType = prodType;
            break;

        case PropertyType.quadrant:
            requestData.Quadrants = [valueToGetData];
            requestData.ProductType = prodType;
            break;

        case PropertyType.blockProfile:
            requestData.BlockProfileNumbers = [valueToGetData];
            requestData.ProductType = prodType;
            break;
        case PropertyType.cityWide:
        case PropertyType.none:
            {
                const fileType: string = restProps.data.productAttributes.result?.find(x => x.Name === fileTypePA)?.TextValue || '';
                const productName: string =
                    restProps.data.productAttributes.result?.find(x => x.Name === tibcoProductNamePA)?.TextValue || '';
                requestData.FileType = fileType;
                requestData.ProductName = productName;
                requestData.ProductType = prodType;
            }
            break;
        case PropertyType.ats:
            {
                const fileType: string = restProps.data.productAttributes.result?.find(x => x.Name === fileTypePA)?.TextValue || '';
                const productName: string =
                    restProps.data.productAttributes.result?.find(x => x.Name === tibcoProductNamePA)?.TextValue || '';
                requestData.ATSNos = [valueToGetData];
                requestData.FileType = fileType;
                requestData.ProductName = productName;
                requestData.ProductType = prodType;
            }
            break;

        default:
            return null;
    }
    const result = await getDocumentResult(requestData, ctx);
    if (result.documents) {
        const docCheck = result.documents.DocumentData?.length === 1;
        const docList = result.documents.DocumentData?.map(doc => {
            return {
                documentId: doc.DocumentId,
                documentName: doc.DocumentName,
                isChecked: docCheck
            };
        });
        return docList;
    } else {
        const errorMessage = generateErrorMessage(result, product, restProps.resources);
        displayErrorMessage(errorMessage);
        result.status >= 5000 || clearValues();
    }

    return null;
};
export default getDocumentList;
